import React, { MouseEventHandler, useEffect, useRef, useState } from 'react'
import classes from './ProjectDetailsFlexListItem.module.scss'
import { FlexProjectItem } from '../../ProjectDetails'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTag } from '@fortawesome/free-solid-svg-icons'
import { Tooltip } from 'primereact/tooltip'
import { OverlayPanel } from 'primereact/overlaypanel'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import CustomOverlayPanel from '../../../../../shared/overlay-panel/CustomOverlayPanel'
import { ProgressSpinner } from 'primereact/progressspinner'
import ProjectService from '../../../../../services/ProjectService'
import { ItemList, SampleInformation } from '../../../../../models/flex/FlexSampleTemplate.models'

export interface ProjectDetailsFlexListItemProps {
  item?: FlexProjectItem
  onClick?: MouseEventHandler | undefined
}

const ProjectDetailsFlexListItem = (props: ProjectDetailsFlexListItemProps) => {
  const overlayRef = useRef<OverlayPanel>(null)
  const [isLoading, setIsLoading] = useState<boolean>()
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>()
  const [itemList, setItemList] = useState<ItemList>()
  const [samples, setSamples] = useState<SampleInformation[]>()
  const [hasSample, setHasSample] = useState<boolean>(false)

  const containerClassName = clsx(`${classes['projectDetailsFlexListItem__container']}`, {
    [classes['projectDetailsFlexListItem__container-active']]: props.item?.isActive,
  })

  const flexProjectId = props.item?.id ?? ''
  const labwareUrl = `${process.env.REACT_APP_LW_URL}?fid=${flexProjectId}&sid=250`

  const hideAllOverlays = () => {
    document
      .querySelectorAll('.p-overlaypanel')
      .forEach(el => ((el as HTMLElement).style.display = 'none'))
  }

  const GetFlexSampleStatus = async () => {
    setIsLoading(true)
    var response = await ProjectService.getFlexSampleStatus(flexProjectId)

    if (response && response?.data) {
      var itemLists = response.data?.itemList ?? []
      if (itemLists.length > 0) {
        var item = itemLists[0]
        setItemList(item)
        setHasSample(item.projectSampleStatusIcon?.toLowerCase() !== 'noicon')
      } else setHasSample(false)
    }

    setIsLoading(false)
  }

  const showOverlay = async (event: React.MouseEvent<HTMLSpanElement>) => {
    hideAllOverlays()
    setIsDetailLoading(true)

    event.stopPropagation()
    overlayRef.current?.toggle(event)

    var response = await ProjectService.getFlexSampleDetail(flexProjectId)
    if (response && response?.data) {
      const sampleDetail = response?.data
      setSamples(sampleDetail?.samples)
    }

    setTimeout(() => {
      setIsDetailLoading(false)
    }, 300)
  }

  const headerSampleName = (
    <>
      <div>Sample Names</div>
      <div>
        <a
          href={labwareUrl}
          target="_blank"
          style={{ color: '#3fa9f5 !important', paddingTop: '10px' }}
        >
          Manage Lab/Samples
        </a>
      </div>
    </>
  )

  const getSampleTooltip = (color?: string) => {
    switch (color) {
      case 'green':
        return 'At least one sample has been received.'
      case 'blue':
        return 'Disposition has been set for all samples'
      case 'gray':
        return 'Sample(s) requested, none yet received.'
      default:
        return ''
    }
  }

  useEffect(() => {
    GetFlexSampleStatus()
  }, [])

  return (
    <>
      <div className={containerClassName} onClick={props.onClick}>
        <div>
          <h4 className={`${classes['header']}`} title={props.item?.name}>
            {props.item?.name}
          </h4>
          <p className={`${classes['status']}`} title={props.item?.status}>
            {props.item?.status}
          </p>
        </div>

        {isLoading && (
          <div>
            <ProgressSpinner
              style={{ width: '15px', height: '15px' }}
              strokeWidth="3"
              animationDuration=".5s"
            />
          </div>
        )}

        {hasSample && (
          <div>
            <span onClick={showOverlay} style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon
                icon={faTag}
                size="xl"
                id={`tag-icon${flexProjectId}`}
                className={
                  classes[`tag-icon-${itemList?.projectSampleStatusIcon?.toLocaleLowerCase()}`]
                }
              />
            </span>
            <Tooltip
              target={`#tag-icon${flexProjectId}`}
              position="right"
              content={getSampleTooltip(itemList?.projectSampleStatusIcon?.toLocaleLowerCase())}
              className="custom-tooltip"
            ></Tooltip>
          </div>
        )}
      </div>

      <CustomOverlayPanel
        reference={overlayRef}
        showCloseIcon={false}
        styleClass="grid-action-popover no-fixed-width"
      >
        {isDetailLoading && (
          <div className="loading-container">
            <ProgressSpinner
              style={{ width: '30px', height: '30px' }}
              strokeWidth="3"
              animationDuration=".5s"
            />
          </div>
        )}

        {!isDetailLoading && (
          <DataTable value={samples} showGridlines>
            <Column field="description" header={headerSampleName} />
            <Column field="projectNumber" header="Aurora Project Number" />
            <Column field="locationDescription" header="Location" />
            <Column field="status" header="Status" />
            <Column field="receivedDate" header="Received Date" className="w-40" />
            <Column field="dueDate" header="Due Date" className="w-40" />
          </DataTable>
        )}
      </CustomOverlayPanel>
    </>
  )
}

export default ProjectDetailsFlexListItem
